import { workspaceFetch as fetch } from "@/lib/workspace-fetch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toApiLocale } from "@/i18n/locales.mjs";
import { useSession } from "@/lib/auth-api";
import { pollingInterval, usePollingActivity } from "./polling";

export type NotificationItem = {
  id: string;
  kind?: string;
  title: string;
  message: string;
  resourceType?: string;
  resourceId?: string | null;
  actionUrl?: string;
  status: "UNREAD" | "READ" | string;
  createdAt?: string;
};

export type NotificationResponse = {
  ok: true;
  notifications: NotificationItem[];
  unreadCount?: number;
  deployment?: { id: string; scope: string; startedAt: string } | null;
};

export const notificationKeys = {
  all: ["notifications"] as const,
  inbox: (userId?: string) => ["notifications", "inbox", userId] as const,
  config: ["notifications", "config"] as const,
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin", cache: "no-store", ...init });
  const payload = await response.json().catch(() => ({})) as T & { detail?: string; error?: string; ok?: boolean };
  if (!response.ok || payload.ok === false) throw new Error(payload.error || payload.detail || "NOTIFICATION_REQUEST_FAILED");
  return payload;
}

export function useNotifications(enabled = true) {
  const active = usePollingActivity();
  const session = useSession();
  return useQuery({
    queryKey: notificationKeys.inbox(session.data?.user.id || session.data?.user.email),
    queryFn: () => request<NotificationResponse>("/api/v1/notifications"),
    enabled: enabled && !!session.data?.user,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => active ? pollingInterval(30_000, query.state.fetchFailureCount, session.data?.user.id || session.data?.user.email || "notifications") : false,
  });
}

export function unreadNotificationCount(response?: NotificationResponse) {
  return response?.unreadCount ?? response?.notifications.filter(item => item.status === "UNREAD").length ?? 0;
}

export function useNotificationConfig(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.config,
    queryFn: () => request<{ ok: true; enabled: boolean; vapidPublicKey: string }>("/api/v1/notifications/config"),
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  const session = useSession();
  const inboxKey = notificationKeys.inbox(session.data?.user.id || session.data?.user.email);
  return useMutation({
    mutationKey: ["notifications", "read"],
    mutationFn: (notificationIds: string[] | "ALL") => request<{ ok: true; updated: number }>("/api/v1/notifications/read", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(notificationIds === "ALL" ? {all: true} : { notificationIds }),
      keepalive: true,
    }),
    onMutate: async (notificationIds) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const previous = queryClient.getQueryData<NotificationResponse>(inboxKey);
      queryClient.setQueryData<NotificationResponse>(inboxKey, (current) => current ? {
        ...current,
        unreadCount: notificationIds === "ALL" ? 0 : Math.max(0, unreadNotificationCount(current) - current.notifications.filter(item => item.status === "UNREAD" && notificationIds.includes(item.id)).length),
        notifications: current.notifications.map((item) => notificationIds === "ALL" || notificationIds.includes(item.id) ? { ...item, status: "READ" } : item),
      } : current);
      return { previous, inboxKey };
    },
    onError: (_error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(context.inboxKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export async function subscribeToPushNotifications(vapidPublicKey: string, locale: string) {
  const padding = "=".repeat((4 - vapidPublicKey.length % 4) % 4);
  const raw = atob((vapidPublicKey + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const applicationServerKey = Uint8Array.from(raw, (character) => character.charCodeAt(0));
  const registration = await navigator.serviceWorker.register("/sw.js");
  const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  return request("/api/v1/notifications/subscriptions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...subscription.toJSON(), locale: toApiLocale(locale) }),
  });
}
